import { WidgetVisibilityContext } from 'components/Widgets/WidgetVisibilityContext';
import { useContext, useState } from 'react';
import { DOPPLER_CONFIG } from '../../config/dopplerRadar.config.js';
import useDopplerMapLocation from '../../hooks/useDopplerMapLocation.js';
import useDopplerPlayback from '../../hooks/useDopplerPlayback.js';
import { canUseDirectCustomWeatherTiles } from '../../utils/dopplerTile.utils.js';
import DopplerLoadingOverlay from './components/DopplerLoadingOverlay/DopplerLoadingOverlay.jsx';
import DopplerMapControls from './components/DopplerMapControls/DopplerMapControls.jsx';
import DopplerRadarLayers from './components/DopplerRadarLayers/DopplerRadarLayers.jsx';

const DOPPLER_MAX_BOUNDS = DOPPLER_CONFIG.maxBounds;
const DEFAULT_DOPPLER_ZOOM = DOPPLER_CONFIG.defaultZoom;
const MIN_DOPPLER_ZOOM = DOPPLER_CONFIG.minZoom;

export default function DopplerMapContent({
  compact = false,
  scrollZoom = true,
  useSinglePanelCompactControls = false,
}) {
  const { mapBounds, mapCenter } = useDopplerMapLocation();
  // Without a CustomWeather token and base URL the radar has no tile source. The Fusion proxy is only
  // a fallback for failing direct tiles, so the radar shows as unavailable and requests nothing.
  const isRadarAvailable = canUseDirectCustomWeatherTiles();
  const { isVisible: isWidgetVisible = true } = useContext(WidgetVisibilityContext);
  const [bufferState, setBufferState] = useState({
    isPlaybackReady: false,
    showLoadingOverlay: true,
    showViewLoadingOverlay: false,
    viewLoadingReason: null,
    startupReadyFrameCount: 0,
    startupTargetFrameCount: 0,
  });

  const {
    baseTime,
    frames,
    frameCount,
    displayedFrameIndex,
    requestedFrameIndex,
    isPlaying,
    playbackSpeed,
    setFrameCount,
    setIsPlaying,
    setPlaybackSpeed,
    requestFrame,
    handleDisplayedFrameChange,
    setIsTransitioning,
  } = useDopplerPlayback({
    isPlaybackReady: bufferState.isPlaybackReady,
    isActive: isWidgetVisible,
  });
  const mapWrapperClassName = `position-relative h-100 w-100 rounded-2 overflow-hidden${
    compact ? ' p-2' : ''
  }`;
  const mapProps = {
    mapCenter,
    bounds: mapBounds,
    zoom: DEFAULT_DOPPLER_ZOOM,
    minZoom: MIN_DOPPLER_ZOOM,
    maxZoom: DOPPLER_CONFIG.maxZoom,
    maxBounds: DOPPLER_MAX_BOUNDS,
    maxBoundsViscosity: DOPPLER_CONFIG.maxBoundsViscosity,
    disableWorldWrap: DOPPLER_CONFIG.disableWorldWrap,
    scrollZoom,
  };
  const playbackProps = {
    frames,
    frameCount,
    displayedFrameIndex,
    isPlaying,
    isPlaybackReady: bufferState.isPlaybackReady,
    isRadarAvailable,
    playbackSpeed,
    onTogglePlayback: () => setIsPlaying((isCurrentlyPlaying) => !isCurrentlyPlaying),
    onSpeedChange: setPlaybackSpeed,
    onFrameCountChange: setFrameCount,
    onScrub: requestFrame,
  };

  return (
    <div className={mapWrapperClassName}>
      <DopplerMapControls
        compact={compact}
        mapProps={mapProps}
        playbackProps={playbackProps}
        useSinglePanelCompactControls={useSinglePanelCompactControls}
      >
        {isRadarAvailable && (
          <DopplerRadarLayers
            baseTime={baseTime}
            frames={frames}
            displayedFrameIndex={displayedFrameIndex}
            requestedFrameIndex={requestedFrameIndex}
            onDisplayedFrameChange={handleDisplayedFrameChange}
            onTransitionStateChange={setIsTransitioning}
            onBufferStateChange={setBufferState}
          />
        )}
      </DopplerMapControls>

      {!isRadarAvailable && (
        <div
          role="status"
          className="position-absolute top-0 end-0 z-3 m-3 px-3 py-2 rounded-3 bg-dark bg-opacity-75 text-white shadow-sm small fw-semibold"
          style={{ pointerEvents: 'none' }}
        >
          Radar unavailable
        </div>
      )}

      {isRadarAvailable &&
        (bufferState.showLoadingOverlay || bufferState.showViewLoadingOverlay) && (
          <DopplerLoadingOverlay
            showViewLoadingOverlay={bufferState.showViewLoadingOverlay}
            viewLoadingReason={bufferState.viewLoadingReason}
            startupReadyFrameCount={bufferState.startupReadyFrameCount}
            startupTargetFrameCount={bufferState.startupTargetFrameCount}
          />
        )}
    </div>
  );
}
